import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Check, X, ArrowRight, Trash2, Plus, CalendarPlus, CalendarDays, Search } from "lucide-react";
import { toast } from "sonner";

function formatBatchDate(d) {
  if (!d) return null;
  const dt = new Date(d + "T00:00:00");
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

function BatchDateControl({ batch, isMaster, onSave }) {
  const [val, setVal] = useState(batch.date || "");
  const changed = val !== (batch.date || "");
  if (!isMaster) {
    return (
      <div className="flex items-center gap-1.5 text-xs mb-3 -mt-1" data-testid={`batch-date-${batch.label}`}>
        <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
        {batch.date ? (
          <span className="text-slate-600 font-medium">{formatBatchDate(batch.date)}</span>
        ) : (
          <span className="text-slate-400">No visit date set</span>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 mb-3 -mt-1">
      <CalendarDays className="h-3.5 w-3.5 text-slate-400 shrink-0" />
      <Input
        type="date"
        data-testid={`batch-date-input-${batch.label}`}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="h-7 text-xs px-2 py-0"
      />
      {changed && (
        <Button
          size="sm"
          data-testid={`batch-date-save-${batch.label}`}
          className="h-7 px-2 text-[11px] bg-teal-600 hover:bg-teal-700 text-white rounded-md"
          onClick={() => onSave(batch.label, val)}
        >
          Save
        </Button>
      )}
    </div>
  );
}

function Column({ title, tone, count, capacity, children, testid, onDrop, onDragOver, onDragLeave, dropActive }) {
  const pct = capacity ? Math.min(100, Math.round((count / capacity) * 100)) : 0;
  const over = capacity && count > capacity;
  return (
    <Card
      data-testid={testid}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      className={`p-4 border flex flex-col min-h-[120px] transition-all ${dropActive ? "border-teal-400 border-dashed ring-2 ring-teal-400/40 bg-teal-50/50" : "border-slate-200 bg-slate-50/60"}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${tone}`} />
          <h3 className="font-heading font-semibold text-sm text-slate-800">{title}</h3>
        </div>
        <span className={`text-xs tabular-nums font-medium ${over ? "text-red-600" : "text-slate-500"}`}>
          {count}
          {capacity ? `/${capacity}` : ""}
        </span>
      </div>
      {capacity ? <Progress value={pct} className="h-1.5 mb-3" /> : <div className="mb-1" />}
      <div className="space-y-2 flex-1">{children}</div>
    </Card>
  );
}

export default function BatchBoard({ patients, batches, isMaster, reload, readOnly = false }) {
  const canManageBatches = !readOnly; // master and client can manage; disabled in past-month view
  const [newLabel, setNewLabel] = useState("");
  const [newCap, setNewCap] = useState(15);
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState("");
  const draggedRef = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const moveTo = async (label) => {
    const id = draggedRef.current;
    draggedRef.current = null;
    setDragOver(null);
    if (!id) return;
    try {
      await api.post(`/patients/${id}/assign-batch`, { batch: label });
      toast.success(label ? `Moved to Batch ${label}` : "Moved to Unassigned");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const dropProps = (key, label) => ({
    onDragOver: (e) => {
      if (!draggedRef.current || readOnly) return;
      e.preventDefault();
      setDragOver(key);
    },
    onDragLeave: () => setDragOver((d) => (d === key ? null : d)),
    onDrop: (e) => {
      if (readOnly) return;
      e.preventDefault();
      moveTo(label);
    },
    dropActive: dragOver === key,
  });

  const run = async (fn, msg) => {
    try {
      const res = await fn();
      if (res?.data?.moved_to === "next_month") toast.info("No-show — moved to next month");
      else if (res?.data?.moved_to) toast.info(`No-show — moved to Batch ${res.data.moved_to}`);
      else if (msg) toast.success(msg);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const q = search.trim().toLowerCase();
  const match = (p) => !q || [p.name, p.register_number, p.phone].some((v) => (v || "").toLowerCase().includes(q));
  const membersOf = (label) =>
    patients
      .filter((p) => !p.carryover_next_month && match(p) && (p.batch === label || p.extra_batch === label))
      .map((p) => ({ ...p, secondary: p.batch !== label && p.extra_batch === label }));
  const unassigned = patients.filter((p) => !p.batch && !p.carryover_next_month && match(p));
  const carryover = patients.filter((p) => p.carryover_next_month && match(p));
  const tones = ["bg-teal-500", "bg-amber-500", "bg-sky-500", "bg-fuchsia-500"];

  const addBatch = async () => {
    try {
      await api.post("/batches", { label: newLabel.trim(), capacity: Number(newCap) });
      toast.success(`Batch ${newLabel} created`);
      setNewLabel("");
      setNewCap(15);
      setAddOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveBatchDate = async (label, date) => {
    try {
      await api.patch(`/batches/${label}`, { date: date || "" });
      toast.success(`Batch ${label} visit date updated`);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const PatientChip = ({ p }) => (
    <div
      data-testid={`board-chip-${p.register_number}`}
      draggable={!readOnly && !p.secondary}
      onDragStart={(e) => {
        draggedRef.current = p.id;
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragEnd={() => setDragOver(null)}
      className={`flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 shadow-xs transition-all ${!readOnly && !p.secondary ? "cursor-move hover:shadow-md hover:border-teal-300 active:scale-[0.98]" : ""}`}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">
          {p.name}
          {p.secondary && (
            <span className="ml-1 align-middle rounded-full bg-violet-100 text-violet-800 text-[9px] font-semibold px-1.5 py-0.5">2nd</span>
          )}
        </p>
        <p className="text-[11px] text-slate-500 font-mono">
          {p.register_number}{p.orthodontist ? ` · ${p.orthodontist}` : ""}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {readOnly ? (
          <span className={`text-[10px] font-semibold ${p.attendance === "visited" ? "text-emerald-600" : p.attendance === "no_show" ? "text-red-600" : "text-slate-400"}`}>
            {p.attendance === "visited" ? "Visited" : p.attendance === "no_show" ? "No-show" : "Pending"}
          </span>
        ) : p.secondary ? (
          <span className="text-[10px] text-violet-600 font-semibold">2nd visit</span>
        ) : (
          <>
            {p.attendance === "visited" && <span className="text-[10px] text-emerald-600 font-semibold">✓</span>}
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 text-emerald-600 hover:bg-emerald-50"
              title="Visited"
              onClick={() => run(() => api.post(`/patients/${p.id}/attendance`, { status: "visited" }), "Marked visited")}
            >
              <Check className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 text-red-600 hover:bg-red-50"
              title="No-show → next batch"
              onClick={() => run(() => api.post(`/patients/${p.id}/attendance`, { status: "no_show" }))}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  );

  const MoveChip = ({ p }) => (
    <div
      draggable={!readOnly}
      onDragStart={(e) => {
        draggedRef.current = p.id;
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragEnd={() => setDragOver(null)}
      className={`flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 shadow-xs transition-all ${!readOnly ? "cursor-move hover:shadow-md hover:border-teal-300" : ""}`}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
        <p className="text-[11px] text-slate-500 font-mono">{p.register_number}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!readOnly && batches.map((b) => (
          <Button
            key={b.label}
            size="sm"
            variant="outline"
            data-testid={`promote-${p.register_number}-to-${b.label}`}
            className="h-6 px-2 text-[11px]"
            onClick={() => run(() => api.post(`/patients/${p.id}/assign-batch`, { batch: b.label }), `Moved to Batch ${b.label}`)}
          >
            {b.label} <ArrowRight className="h-3 w-3 ml-0.5" />
          </Button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          data-testid="batch-search-input"
          placeholder="Search patients across batches"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>
      {canManageBatches && (
        <div className="flex flex-wrap items-center gap-3">
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-batch-button" variant="outline" className="rounded-lg">
                <Plus className="h-4 w-4 mr-1.5" /> New Batch
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white sm:max-w-sm">
              <DialogHeader>
                <DialogTitle className="font-heading">Create Batch</DialogTitle>
                <DialogDescription>Add a new appointment batch and set its capacity.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Batch Label</Label>
                  <Input data-testid="new-batch-label" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="C" />
                </div>
                <div className="space-y-1.5">
                  <Label>Capacity</Label>
                  <Input data-testid="new-batch-capacity" type="number" value={newCap} onChange={(e) => setNewCap(e.target.value)} />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="new-batch-submit" onClick={addBatch} className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg">
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="new-month-button" variant="outline" className="rounded-lg">
                <CalendarPlus className="h-4 w-4 mr-1.5" /> Start New Month
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-white">
              <AlertDialogHeader>
                <AlertDialogTitle>Start a new month?</AlertDialogTitle>
                <AlertDialogDescription>
                  Carryover patients move into the first batch, everyone else becomes unassigned,
                  and all attendance resets to pending. Missed counts are kept.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="new-month-confirm"
                  onClick={() => run(() => api.post("/cycle/new-month"), "New month started")}
                  className="bg-primary hover:bg-slate-800"
                >
                  Confirm
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {batches.map((b, i) => {
          const list = membersOf(b.label);
          return (
            <Column
              key={b.label}
              testid={b.label === batches[0]?.label ? "batch-a-container" : b.label === batches[1]?.label ? "batch-b-container" : `batch-${b.label}-container`}
              title={`Batch ${b.label}`}
              tone={tones[i % tones.length]}
              count={list.length}
              capacity={b.capacity}
              {...dropProps(b.label, b.label)}
            >
              <BatchDateControl
                key={b.date || "none"}
                batch={b}
                isMaster={canManageBatches}
                onSave={saveBatchDate}
              />
              {list.length === 0 ? (
                <p className="text-xs text-slate-400 py-3 text-center">Empty</p>
              ) : (
                list.map((p) => (
                  <div key={p.id || p.register_number} className="space-y-1">
                    <PatientChip p={p} />
                  </div>
                ))
              )}
              {canManageBatches && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-batch-${b.label}`} className="text-[11px] text-red-500 hover:underline mt-2 inline-flex items-center">
                      <Trash2 className="h-3 w-3 mr-1" /> Delete batch
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Batch {b.label}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Patients in this batch will become unassigned.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => run(() => api.delete(`/batches/${b.label}`), "Batch deleted")}
                        className="bg-destructive hover:bg-destructive/90"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </Column>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Column testid="unassigned-container" title="Unassigned" tone="bg-slate-400" count={unassigned.length} {...dropProps("__unassigned__", null)}>
          {unassigned.length === 0 ? (
            <p className="text-xs text-slate-400 py-3 text-center">Everyone is scheduled</p>
          ) : (
            unassigned.map((p) => <MoveChip key={p.id || p.register_number} p={p} />)
          )}
        </Column>
        <Column testid="carryover-container" title="Next Month Carryover" tone="bg-violet-500" count={carryover.length}>
          {carryover.length === 0 ? (
            <p className="text-xs text-slate-400 py-3 text-center">None</p>
          ) : (
            carryover.map((p) => <MoveChip key={p.id || p.register_number} p={p} />)
          )}
        </Column>
      </div>
    </div>
  );
}
